/**
 * SupportPage — list of the user's own support tickets.
 *
 * Route: `/support` (ProtectedRoute).
 *
 * Each ticket row is tappable → /support/:id (detail with messages
 * + reply). The header has a prominent "New ticket" CTA → /support/new.
 *
 * Sort: by `updatedAt` desc, so a fresh admin reply on an old ticket
 * surfaces it back to the top.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import ForumIcon from '@mui/icons-material/Forum';

import LanguageToggle from '@/components/LanguageToggle';
import { supportApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatDate } from '@/utils/date';

function statusChipColor(status) {
  switch (status) {
    case 'open':        return 'warning';
    case 'in_progress': return 'info';
    case 'resolved':    return 'success';
    case 'closed':      return 'default';
    default:            return 'default';
  }
}

function TicketCard({ ticket, language, t, onOpen }) {
  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onOpen(ticket.id)}>
        <CardContent>
          {/* Top: subject (truncate) + status */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ticket.subject}
            </Typography>
            {ticket.unreadCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={t('common.unreadCount', { count: ticket.unreadCount })}
                sx={{ flexShrink: 0, fontWeight: 700 }}
              />
            )}
            <Chip
              size="small"
              color={statusChipColor(ticket.status)}
              label={t(`support.status.${ticket.status}`, {
                defaultValue: ticket.status,
              })}
              variant="filled"
            />
          </Stack>

          {/* Category */}
          <Typography variant="caption" color="text.secondary" display="block">
            {t(`support.category.${ticket.category}`, {
              defaultValue: ticket.category,
            })}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Footer: message count + last updated */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ color: 'text.secondary', gap: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ForumIcon fontSize="small" />
              <Typography variant="caption">
                {t('support.list.messageCount', { count: ticket.messages?.length ?? 0 })}
              </Typography>
            </Stack>
            <Typography variant="caption">
              {formatDate(ticket.updatedAt, language, { style: 'datetime' })}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function SupportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: () => supportApi.listTickets(),
    // Replies from admin can land anytime — short stale so the list
    // refreshes when the user re-enters the page.
    staleTime: 30 * 1000,
  });

  const tickets = data?.data?.tickets ?? [];

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <ContactSupportIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('support.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Subtitle + New ticket CTA */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
        >
          {!isError && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {t('support.subtitle')}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/support/new')}
            sx={{ flexShrink: 0 }}
          >
            {t('support.newTicket')}
          </Button>
        </Stack>

        {/* Loading */}
        {isLoading && (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={120}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('support.error.loadFailed')}
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !isError && tickets.length === 0 && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <InboxIcon sx={{ fontSize: 56, opacity: 0.4 }} />
            <Typography variant="subtitle1" textAlign="center" fontWeight={600}>
              {t('support.empty.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 320 }}
            >
              {t('support.empty.message')}
            </Typography>
          </Stack>
        )}

        {/* List */}
        {!isLoading && !isError && tickets.length > 0 && (
          <Stack spacing={2}>
            {tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                language={language}
                t={t}
                onOpen={(id) => navigate(`/support/${id}`)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default SupportPage;
